import {
  INTRANET_REACTION_EMOJI,
  INTRANET_REACTION_KINDS,
  INTRANET_REACTION_LABEL,
  intranetReactionListSchema,
  type IntranetReactionKind,
  type IntranetReactionList,
  type Voice,
} from '@gb-ticket/shared-types';
import { useQuery } from '@tanstack/react-query';
import { ThumbsUpIcon } from 'lucide-react';
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
  type Ref,
} from 'react';
import { createPortal } from 'react-dom';

import { apiFetch } from '@/lib/api';
import { cn } from '@/lib/utils';
import { useVoice } from '@/platform/voice/use-voice';
import {
  ColleagueNameButton,
  colleagueVisibleName,
} from '@/products/intranet/components/colleague-name-button';
import { ColleaguePortrait } from '@/products/intranet/features/intranet/colleague-portrait';

const ACTION =
  'inline-flex min-h-11 items-center gap-1 rounded-md px-2 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none';
const CLOSE_MS = 180;

/** Thumbs-up beside a like. Shown only when the on-screen count is at least one. */
export const IntranetLikeNamesButton = forwardRef(function IntranetLikeNamesButton(
  {
    count,
    emojis = [],
    expanded,
    onClick,
  }: {
    count: number;
    emojis?: readonly string[];
    expanded?: boolean;
    onClick?: (event: MouseEvent<HTMLButtonElement>) => void;
  },
  ref: Ref<HTMLButtonElement>,
) {
  if (count < 1) {
    return null;
  }
  return (
    <button
      ref={ref}
      type="button"
      aria-label="Wem gefällt das"
      aria-expanded={expanded}
      aria-haspopup={expanded === undefined ? undefined : 'dialog'}
      className={ACTION}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      {emojis.length > 0 ? (
        <span aria-hidden className="inline-flex items-center -space-x-0.5">
          {emojis.map((emoji) => (
            <span key={emoji}>{emoji}</span>
          ))}
        </span>
      ) : (
        <ThumbsUpIcon className="size-4" aria-hidden />
      )}
    </button>
  );
});

/** Anchored reaction list for a news post, comment, or reply. Not a route. */
export function IntranetLikeNames({
  count,
  emojis,
  listPath,
}: {
  count: number;
  emojis?: readonly string[];
  listPath: string;
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [filter, setFilter] = useState<'all' | IntranetReactionKind>('all');
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);
  const voice = useVoice();
  const list = useQuery({
    queryKey: ['intranet', 'reaction-list', listPath],
    queryFn: () => apiFetch(listPath, intranetReactionListSchema),
    enabled: open || visible,
  });

  function show() {
    setFilter('all');
    setVisible(true);
    setOpen(true);
  }

  function hide() {
    setOpen(false);
  }

  useEffect(() => {
    if (open || !visible) {
      return;
    }
    const timer = window.setTimeout(() => setVisible(false), CLOSE_MS);
    return () => window.clearTimeout(timer);
  }, [open, visible]);

  useEffect(() => {
    if (!visible) {
      return;
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        hide();
      }
    }
    function onPointerDown(event: PointerEvent) {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (buttonRef.current?.contains(target) || popupRef.current?.contains(target)) {
        return;
      }
      hide();
    }
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [visible]);

  return (
    <>
      <IntranetLikeNamesButton
        ref={buttonRef}
        count={count}
        emojis={emojis}
        expanded={open}
        onClick={() => {
          if (open) {
            hide();
            return;
          }
          show();
        }}
      />
      {visible
        ? createPortal(
            <div
              className={cn(
                'fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 duration-200 motion-reduce:animate-none',
                open ? 'animate-in fade-in-0' : 'pointer-events-none animate-out fade-out-0',
              )}
              onMouseDown={hide}
            >
              <div
                ref={popupRef}
                role="dialog"
                aria-modal="true"
                aria-label="Reaktionen"
                className={cn(
                  'max-h-[80dvh] w-full max-w-md overflow-y-auto rounded-lg border bg-popover p-3 text-sm text-foreground shadow-md duration-200 motion-reduce:animate-none',
                  open
                    ? 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2'
                    : 'animate-out fade-out-0 zoom-out-95',
                )}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <div className="flex items-center justify-between gap-2 px-1">
                  <p className="text-base font-semibold">Reaktionen</p>
                  <button
                    type="button"
                    aria-label="Schließen"
                    className="inline-flex size-11 items-center justify-center rounded-md text-lg focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
                    onClick={hide}
                  >
                    ×
                  </button>
                </div>
                {list.isError ? (
                  <p className="px-2 py-2 text-muted-foreground">
                    Die Liste konnte nicht geladen werden.
                  </p>
                ) : list.data?.items.length === 0 ? (
                  <p className="px-2 py-2 text-muted-foreground">Niemand</p>
                ) : list.data ? (
                  <ReactionPeople
                    items={list.data.items}
                    filter={filter}
                    voice={voice}
                    onFilter={setFilter}
                    onOpenPerson={() => setOpen(false)}
                  />
                ) : null}
              </div>
            </div>,
            document.body,
          )
        : null}
    </>
  );
}

function ReactionPeople({
  items,
  filter,
  voice,
  onFilter,
  onOpenPerson,
}: {
  items: IntranetReactionList['items'];
  filter: 'all' | IntranetReactionKind;
  voice: Voice;
  onFilter: (next: 'all' | IntranetReactionKind) => void;
  onOpenPerson: () => void;
}) {
  const counts = new Map<IntranetReactionKind, number>();
  for (const person of items) {
    counts.set(person.reaction, (counts.get(person.reaction) ?? 0) + 1);
  }
  const shown = filter === 'all' ? items : items.filter((person) => person.reaction === filter);
  return (
    <>
      <div
        className="flex flex-wrap gap-1 px-1 pb-2"
        role="group"
        aria-label="Nach Reaktion filtern"
      >
        <FilterChip pressed={filter === 'all'} onClick={() => onFilter('all')}>
          Alle
        </FilterChip>
        {INTRANET_REACTION_KINDS.filter((kind) => (counts.get(kind) ?? 0) > 0).map((kind) => (
          <FilterChip key={kind} pressed={filter === kind} onClick={() => onFilter(kind)}>
            <span aria-hidden>{INTRANET_REACTION_EMOJI[kind]}</span>
            <span className="sr-only">{INTRANET_REACTION_LABEL[kind]} </span>
            {counts.get(kind)}
          </FilterChip>
        ))}
      </div>
      <ul>
        {shown.map((person) => (
          <li key={person.userId} onClick={onOpenPerson}>
            <ColleagueNameButton
              userId={person.userId}
              displayName={person.displayName}
              className="flex min-h-11 w-full items-center gap-3 px-2 py-1 text-left hover:no-underline"
            >
              <span className="relative shrink-0">
                <ColleaguePortrait
                  displayName={person.displayName}
                  profilePhotoUrl={person.profilePhotoUrl}
                  className="size-10 text-xs"
                />
                <span className="absolute -right-1 -bottom-1 text-sm leading-none" aria-hidden>
                  {INTRANET_REACTION_EMOJI[person.reaction]}
                </span>
              </span>
              <span className="min-w-0">
                <span className="block truncate font-medium">
                  {colleagueVisibleName(person.displayName, voice)}
                </span>
                {person.departmentName ? (
                  <span className="block truncate text-xs text-muted-foreground">
                    {person.departmentName}
                  </span>
                ) : null}
              </span>
            </ColleagueNameButton>
          </li>
        ))}
      </ul>
    </>
  );
}

function FilterChip({
  pressed,
  onClick,
  children,
}: {
  pressed: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={pressed}
      className={cn(
        'inline-flex min-h-9 items-center gap-1 rounded-full px-3 text-sm',
        pressed ? 'bg-primary text-primary-foreground' : 'bg-muted text-foreground',
      )}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick();
      }}
    >
      {children}
    </button>
  );
}
